// no dangerouslySetInnerHTML — titles are user-controlled text (SEC-REQ-4)
//
// V2 source path: customer/midas/components/v2/history/MidasHistoryMachineFiltersV2.tsx
// V1 source: none (greenfield — Phase 4)
// Strategy: scratch
// Verified no-MUI: yes
// Phase: 4
//
// Horizontal chip row: All / Platform / per-machine (one chip per machine in
// the user's fleet that has at least one chat). Single-select. Selection
// triggers a server-side refetch via the parent's onChange handler.
//
// VISUAL PATTERN: matches the Operations Work Board filter chips
// (BoardFilterBar.tsx) — square-ish 6px-radius chips, charcoal-on-cream
// active state, board-bg inactive, gap 12px desktop / 8px tablet+phone.
// Active: bg #0C1117, color #F7F5F0, weight 600.
// Inactive: bg #F2F0EB, color #4A4F57, weight 500, hover bg #E8E5DF.
//
// On phone, the chip row scrolls horizontally — the scrollbar is hidden via
// the standard `scrollbar-hide` pattern (inline style for cross-browser
// safety since Tailwind doesn't ship that utility by default).
import React from "react";

import { HistoryMachineFilter } from "../../../midasHistoryPageSlice";

export interface MachineChip {
  id: string;
  label: string;
}

interface MidasHistoryMachineFiltersV2Props {
  active: HistoryMachineFilter;
  machines: MachineChip[];
  onChange: (next: HistoryMachineFilter) => void;
  /** Translation function. */
  t: (key: string, fallback: string) => string;
}

/**
 * Square-ish chip matching Operations Work Board pattern (BoardFilterBar).
 * Charcoal-on-cream when active, board-bg when inactive. 6px radius.
 */
const Chip: React.FC<{
  isActive: boolean;
  onClick: () => void;
  label: string;
  ariaLabel: string;
}> = ({ isActive, onClick, label, ariaLabel }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={isActive}
    aria-label={ariaLabel}
    // §4.2 audit: chip radius bumped from 6px (rounded-md) to 8px
    // (rounded-lg) — spec shows softer chips than the Work Board pattern.
    // History page diverges intentionally from BoardFilterBar here.
    className="flex-shrink-0 cursor-pointer whitespace-nowrap rounded-lg border-0 outline-none transition-all duration-150 focus-visible:ring-2 focus-visible:ring-midas-terracotta motion-reduce:transition-none"
    style={{
      // Match BoardFilterBar layout/colors:
      //   padding: 6px 14px desktop / 5px 12px tablet+phone
      //   fontSize: 12px desktop / 11px tablet+phone
      //   colors: #0C1117 / #F7F5F0 active, #F2F0EB / #4A4F57 inactive
      // (Border-radius on the className is now 8px per §4.2 audit.)
      padding: "6px 14px",
      fontFamily: "'Sora', sans-serif",
      fontSize: "12px",
      fontWeight: isActive ? 600 : 500,
      backgroundColor: isActive ? "#0C1117" : "#F2F0EB",
      color: isActive ? "#F7F5F0" : "#4A4F57",
    }}
    onMouseEnter={(e) => {
      if (!isActive) {
        e.currentTarget.style.backgroundColor = "#E8E5DF";
      }
    }}
    onMouseLeave={(e) => {
      if (!isActive) {
        e.currentTarget.style.backgroundColor = "#F2F0EB";
      }
    }}
  >
    {label}
  </button>
);

const MidasHistoryMachineFiltersV2: React.FC<MidasHistoryMachineFiltersV2Props> = ({
  active,
  machines,
  onChange,
  t,
}) => {
  return (
    <div
      role="tablist"
      aria-label={t("midas.historyPage.filterAriaLabel", "Filter by machine")}
      className="flex w-full items-center overflow-x-auto"
      style={{
        // Match Work Board: 12px gap on desktop. We don't have an isPhone
        // signal here so we use the desktop value — this is fine because
        // chips wrap horizontally on small viewports via overflow-x.
        gap: "12px",
        // Hide horizontal scrollbar on phone — chips become a swipe row
        scrollbarWidth: "none",
        msOverflowStyle: "none",
      }}
    >
      <Chip
        isActive={active === "all"}
        onClick={() => onChange("all")}
        label={t("midas.historyPage.filterAll", "All")}
        ariaLabel={t("midas.historyPage.filterAllAria", "Show all conversations")}
      />
      <Chip
        isActive={active === "platform"}
        onClick={() => onChange("platform")}
        label={t("midas.historyPage.filterPlatform", "Platform")}
        ariaLabel={t(
          "midas.historyPage.filterPlatformAria",
          "Show platform conversations only"
        )}
      />
      {machines.map((m) => (
        <Chip
          key={m.id}
          isActive={active === m.id}
          onClick={() => onChange(m.id)}
          label={m.label}
          ariaLabel={`${t("midas.historyPage.filterMachineAria", "Filter by machine")}: ${m.label}`}
        />
      ))}
    </div>
  );
};

export default MidasHistoryMachineFiltersV2;
